import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Pencil, Plus, Trash2, Workflow } from "lucide-react";
import useAuth from "../../../../hooks/useAuth";
import { showToast } from "../../../../utils/showToast";
import {
  createAutomationRule,
  deleteAutomationRule,
  getAutomationRules,
  updateAutomationRule,
} from "../../../../services/helpdeskWorkflowService";
import { isEndpointMissing } from "../../../../services/aiChatService";
import useHelpdeskLabels from "../../../../components/helpdesk/useHelpdeskLabels";
import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { Modal } from "../../../../components/ui/Modal";
import { Select } from "../../../../components/ui/Select";
import { Switch } from "../../../../components/ui/Switch";
import { EmptyState } from "../../../../components/ui/EmptyState";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { RelativeTime } from "../../../../components/ui/RelativeTime";
import { PendingBackendNotice } from "../../../../components/ui/PendingBackendNotice";
import ActionStepsEditor, { withStepKey } from "./ActionStepsEditor";
import ConditionsEditor, { withConditionKey } from "./ConditionsEditor";
import { useWorkflowCatalogs } from "./useWorkflowCatalogs";
import {
  ACTION_DEFS,
  CONDITION_DEFS,
  MAX_ACTIONS,
  MAX_CONDITIONS,
  MAX_RULES,
  RULE_ACTION_TYPES,
  RULE_EVENTS,
  RULE_EVENT_LABELS,
  RULE_NAME_MAX,
  idOf,
  isWorkflowSupervisor,
  serializeAction,
  serializeCondition,
  validateAction,
  validateCondition,
} from "./workflowSchema";
import {
  InlineFormError,
  ListSkeleton,
  LoadErrorBox,
  ReadOnlyNotice,
  RefreshButton,
  SectionHeader,
  toastMutationError,
  workflowErrorMessage,
} from "./workflowUi";

// Reglas de automatización (concepto de Chatwoot): "Cuando <evento> / Si se
// cumplen TODAS <condiciones> / Entonces <acciones>". Corren en el backend al
// llegar un mensaje real del contacto, ANTES del turno de la IA. A propósito no
// escriben al contacto ni cierran casos (eso sigue siendo de la IA y del
// equipo): solo etiquetan, asignan, mueven y anotan.
//
// Crear, editar, activar y borrar es de supervisión; el resto ve las reglas en
// solo lectura.

const EMPTY_DRAFT = {
  name: "",
  enabled: true,
  event: "message_received",
  conditions: [],
  actions: [],
};

/**
 * Validación pura del editor de reglas.
 * Solo acepta los tipos de acción de RULE_ACTION_TYPES: una regla importada o
 * vieja con send_message/resolve no se puede guardar así.
 */
export function validateRuleDraft(draft, t) {
  const errors = {};
  const name = (draft.name || "").trim();
  if (!name) errors.name = t("El nombre es obligatorio.");
  else if (name.length > RULE_NAME_MAX) errors.name = t("Máximo 60 caracteres.");
  if (!RULE_EVENTS.some((e) => e.value === draft.event)) errors.event = t("Elige cuándo corre.");
  if (draft.conditions.length > MAX_CONDITIONS) errors.conditions = t("Máximo 10 condiciones.");
  else if (draft.conditions.some((c) => validateCondition(c, t))) {
    errors.conditions = t("Completa las condiciones marcadas.");
  }
  if (draft.actions.length === 0) errors.actions = t("Agrega al menos una acción.");
  else if (draft.actions.length > MAX_ACTIONS) errors.actions = t("Máximo 10 acciones.");
  else if (draft.actions.some((a) => !RULE_ACTION_TYPES.includes(a.type))) {
    errors.actions = t("Una regla no puede enviar mensajes ni resolver casos.");
  } else if (draft.actions.some((a) => validateAction(a, t))) {
    errors.actions = t("Completa las acciones marcadas.");
  }
  return errors;
}

function sortRules(list) {
  return [...list].sort((a, b) => {
    const ap = Number.isFinite(a?.position) ? a.position : Number.MAX_SAFE_INTEGER;
    const bp = Number.isFinite(b?.position) ? b.position : Number.MAX_SAFE_INTEGER;
    if (ap !== bp) return ap - bp;
    return (a?.name || "").localeCompare(b?.name || "");
  });
}

const hasAssign = (actions) => (actions || []).some((a) => a?.type === "assign");

// Aviso de la decisión #7 del plan: asignar desde una regla no es inocuo.
export function AssignWarning() {
  const { t } = useTranslation();
  return (
    <div
      role="note"
      className="flex items-start gap-2 rounded-lg border border-status-warning bg-status-warning-soft px-3 py-2 text-sm text-status-warning"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        <span className="block font-semibold">{t("Asignar deja el caso en manos de esa persona")}</span>
        <span className="block text-fg">
          {t(
            "Desde ese momento la IA deja de responderle al contacto y el cierre automático no lo toca: si la persona no lo atiende, el caso queda esperando. Úsalo solo cuando alguien vaya a atenderlo de verdad.",
          )}
        </span>
      </span>
    </div>
  );
}

// Bloque del editor: el título dice la parte de la frase ("Cuando…").
function RuleBlock({ title, hint, children }) {
  return (
    <section className="rounded-xl border border-border bg-bg-muted/30 p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-fg">{title}</h3>
        {hint && <p className="text-xs text-fg-muted mt-0.5">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

// ── Editor ───────────────────────────────────────────────────────────────────
function RuleFormModal({ open, mode, draft, onChange, onSubmit, onClose, saving, serverError }) {
  const { t } = useTranslation();
  const [showErrors, setShowErrors] = useState(false);
  const { labels } = useHelpdeskLabels();
  const { data: catalogs, errors: catalogErrors, retry } = useWorkflowCatalogs(
    ["team", "folders", "accounts"],
    open,
  );
  const errors = useMemo(() => validateRuleDraft(draft, t), [draft, t]);

  useEffect(() => {
    if (open) setShowErrors(false);
  }, [open]);

  const handleSubmit = () => {
    setShowErrors(true);
    if (Object.keys(errors).length > 0) return;
    onSubmit();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={mode === "edit" ? t("Editar regla") : t("Nueva regla")}
      description={t(
        "Corre sola cuando pasa el evento, antes de que responda la IA. No le escribe al contacto ni cierra casos.",
      )}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t("Cancelar")}
          </Button>
          <Button className="hd-brand-solid" onClick={handleSubmit} loading={saving}>
            {mode === "edit" ? t("Guardar cambios") : t("Crear regla")}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <InlineFormError id="rule-server-error">{serverError}</InlineFormError>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 md:items-end">
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <FieldLabel className="hd-field-label" htmlFor="rule-name">
                {t("Nombre")}
              </FieldLabel>
              <span className="text-xs tabular-nums text-fg-subtle">
                {(draft.name || "").trim().length} / {RULE_NAME_MAX}
              </span>
            </div>
            <Input
              id="rule-name"
              value={draft.name}
              maxLength={RULE_NAME_MAX}
              invalid={showErrors && !!errors.name}
              onChange={(e) => onChange({ ...draft, name: e.target.value })}
              placeholder={t("Ej: Reclamos de repartidores a bodega")}
            />
            {showErrors && errors.name && <p className="text-xs text-status-error">{errors.name}</p>}
          </div>
          <label className="inline-flex items-center gap-2 h-10 text-sm text-fg">
            <Switch
              checked={!!draft.enabled}
              onChange={(next) => onChange({ ...draft, enabled: next })}
              ariaLabel={t("Regla activa")}
            />
            {draft.enabled ? t("Activa") : t("Pausada")}
          </label>
        </div>

        <RuleBlock title={t("Cuando")}>
          <div className="w-full sm:w-80">
            <Select
              aria-label={t("Evento")}
              value={draft.event}
              onChange={(e) => onChange({ ...draft, event: e.target.value })}
              options={RULE_EVENTS.map((ev) => ({ value: ev.value, label: t(ev.label) }))}
            />
          </div>
          {showErrors && errors.event && <p className="text-xs text-status-error">{errors.event}</p>}
        </RuleBlock>

        <RuleBlock
          title={t("Si se cumplen todas estas condiciones")}
          hint={t("Todas a la vez. El texto se compara sin mayúsculas ni tildes.")}
        >
          <ConditionsEditor
            conditions={draft.conditions}
            onChange={(conditions) => onChange({ ...draft, conditions })}
            labels={labels}
            accounts={catalogs.accounts}
            accountsError={!!catalogErrors.accounts}
            showErrors={showErrors}
          />
          {showErrors && errors.conditions && (
            <p className="text-xs text-status-error">{errors.conditions}</p>
          )}
        </RuleBlock>

        <RuleBlock
          title={t("Entonces")}
          hint={t("En este orden. Si una acción falla, las siguientes no se ejecutan.")}
        >
          {hasAssign(draft.actions) && <AssignWarning />}
          <ActionStepsEditor
            idPrefix="rule-step"
            mode="rule"
            actions={draft.actions}
            onChange={(actions) => onChange({ ...draft, actions })}
            allowedTypes={RULE_ACTION_TYPES}
            labels={labels}
            catalogs={catalogs}
            catalogErrors={catalogErrors}
            onRetryCatalog={retry}
            showErrors={showErrors}
          />
          {showErrors && errors.actions && <p className="text-xs text-status-error">{errors.actions}</p>}
        </RuleBlock>
      </div>
    </Modal>
  );
}

function ruleSummary(rule, t) {
  const conditions = (rule.conditions || [])
    .map((c) => (CONDITION_DEFS[c?.field] ? t(CONDITION_DEFS[c.field].label) : c?.field))
    .join(", ");
  const actions = (rule.actions || [])
    .map((a) => (ACTION_DEFS[a?.type] ? t(ACTION_DEFS[a.type].label) : a?.type))
    .join(" → ");
  return [conditions ? `${t("Si")}: ${conditions}` : t("Siempre"), actions].filter(Boolean).join(" · ");
}

// ── Vista ────────────────────────────────────────────────────────────────────
export default function AutomationSettings() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = isWorkflowSupervisor(user);

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [backendMissing, setBackendMissing] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setBackendMissing(false);
    try {
      setRules(sortRules(await getAutomationRules()));
    } catch (err) {
      if (isEndpointMissing(err)) {
        setBackendMissing(true);
        setRules([]);
      } else {
        setLoadError(workflowErrorMessage(err, t("Revisa tu conexión o vuelve a intentarlo.")));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const atCap = rules.length >= MAX_RULES;

  const openCreate = () => {
    setEditingId(null);
    setDraft({ ...EMPTY_DRAFT, conditions: [], actions: [] });
    setServerError(null);
    setFormOpen(true);
  };

  const openEdit = (rule) => {
    setEditingId(idOf(rule));
    setDraft({
      name: rule.name || "",
      enabled: rule.enabled !== false,
      event: rule.event || EMPTY_DRAFT.event,
      conditions: (rule.conditions || []).filter((c) => CONDITION_DEFS[c?.field]).map(withConditionKey),
      // Las acciones que una regla no admite se conservan para que la
      // validación las marque en vez de perderlas en silencio.
      actions: (rule.actions || []).map(withStepKey),
    });
    setServerError(null);
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setServerError(null);
    const payload = {
      name: draft.name.trim(),
      enabled: !!draft.enabled,
      event: draft.event,
      conditions: draft.conditions.map(serializeCondition),
      actions: draft.actions.map(serializeAction),
    };
    try {
      if (editingId) {
        const saved = await updateAutomationRule(editingId, payload);
        setRules((prev) =>
          sortRules(prev.map((r) => (idOf(r) === editingId ? { ...r, ...payload, ...(saved || {}) } : r))),
        );
        showToast(t("Regla actualizada."), true);
      } else {
        const saved = await createAutomationRule(payload);
        if (saved && idOf(saved)) setRules((prev) => sortRules([...prev, saved]));
        else await load();
        showToast(t("Regla creada."), true);
      }
      setFormOpen(false);
    } catch (err) {
      if (err?.status === 409) {
        setServerError(
          workflowErrorMessage(err, t("Se alcanzó el tope de 30 reglas o ya existe una con ese nombre.")),
        );
      } else if (err?.status === 400 || err?.status === 403) {
        setServerError(workflowErrorMessage(err, t("No se pudo guardar la regla.")));
      } else {
        toastMutationError(err, t("No se pudo guardar la regla."), t);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule, next) => {
    const id = idOf(rule);
    setBusyId(id);
    const previous = rules;
    setRules((prev) => prev.map((r) => (idOf(r) === id ? { ...r, enabled: next } : r)));
    try {
      await updateAutomationRule(id, { enabled: next });
    } catch (err) {
      setRules(previous);
      toastMutationError(err, t("No se pudo cambiar el estado de la regla."), t);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAutomationRule(idOf(deleteTarget));
      setRules((prev) => prev.filter((r) => idOf(r) !== idOf(deleteTarget)));
      showToast(t("Regla eliminada."), true);
      setDeleteTarget(null);
    } catch (err) {
      toastMutationError(err, t("No se pudo eliminar la regla."), t);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        title={t("Automatización")}
        description={t(
          "Reglas que ordenan los casos solas: cuando pasa algo y se cumplen las condiciones, etiquetan, asignan, mueven a carpeta o dejan una nota.",
        )}
        meta={
          !loading && !loadError
            ? t("{{count}} de {{max}} reglas").replace("{{count}}", rules.length).replace("{{max}}", MAX_RULES)
            : null
        }
        actions={
          <>
            <RefreshButton loading={loading} onClick={load} />
            {canEdit && (
              <Button
                className="hd-brand-solid"
                iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
                onClick={openCreate}
                disabled={atCap || loading}
                title={atCap ? t("Se alcanzó el tope de 30 reglas.") : undefined}
              >
                {t("Nueva regla")}
              </Button>
            )}
          </>
        }
      />

      {!canEdit && (
        <ReadOnlyNotice>
          {t("Solo supervisores y administradores pueden crear, editar o pausar reglas.")}
        </ReadOnlyNotice>
      )}

      {backendMissing && (
        <PendingBackendNotice>
          {t("La automatización está en construcción. Los cambios no se guardarán hasta que esté disponible.")}
        </PendingBackendNotice>
      )}

      {loading && <ListSkeleton rows={3} height="h-[76px]" />}

      {!loading && loadError && (
        <LoadErrorBox title={t("No se pudieron cargar las reglas.")} message={loadError} onRetry={load} />
      )}

      {!loading && !loadError && rules.length === 0 && (
        <div className="rounded-xl border border-border bg-bg">
          <EmptyState
            icon={<Workflow aria-hidden="true" />}
            title={t("Todavía no hay reglas")}
            description={t(
              "Por ejemplo: cuando llegue un mensaje que contenga «reembolso», ponle la etiqueta «Reclamo» y muévelo a la carpeta de Finanzas.",
            )}
            action={
              canEdit && !backendMissing ? (
                <Button
                  className="hd-brand-solid"
                  iconLeft={<Plus className="w-4 h-4" aria-hidden="true" />}
                  onClick={openCreate}
                >
                  {t("Crear la primera")}
                </Button>
              ) : null
            }
          />
        </div>
      )}

      {!loading && !loadError && rules.length > 0 && (
        <ul className="rounded-xl border border-border bg-bg divide-y divide-border" aria-label={t("Reglas")}>
          {rules.map((rule) => {
            const id = idOf(rule);
            const enabled = rule.enabled !== false;
            const runCount = Number(rule.run_count) || 0;
            return (
              <li key={id} className="flex items-start gap-3 px-[1rem] py-[0.75rem] sm:px-[1.5rem] sm:py-[1rem]">
                <span className="pt-0.5 shrink-0">
                  <Switch
                    size="sm"
                    checked={enabled}
                    disabled={!canEdit || busyId === id}
                    onChange={(next) => handleToggle(rule, next)}
                    ariaLabel={
                      enabled
                        ? t("Pausar regla {{nombre}}").replace("{{nombre}}", rule.name)
                        : t("Activar regla {{nombre}}").replace("{{nombre}}", rule.name)
                    }
                  />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-semibold text-fg truncate max-w-full max-md:basis-full max-md:whitespace-normal max-md:break-words">{rule.name}</span>
                    <Badge tone="info" className="whitespace-nowrap">{t(RULE_EVENT_LABELS[rule.event] || rule.event || "—")}</Badge>
                    {!enabled && <Badge tone="muted" className="whitespace-nowrap">{t("Pausada")}</Badge>}
                    {hasAssign(rule.actions) && (
                      <Badge
                        tone="warning"
                        className="whitespace-nowrap"
                        title={t("Asignar deja el caso en manos de esa persona")}
                      >
                        {t("Asigna a una persona")}
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-fg-muted truncate" title={ruleSummary(rule, t)}>
                    {ruleSummary(rule, t)}
                  </p>
                  <p className="mt-0.5 text-xs text-fg-muted tabular-nums">
                    {runCount === 1
                      ? t("Se ejecutó 1 vez")
                      : t("Se ejecutó {{count}} veces").replace("{{count}}", runCount)}
                    {rule.last_run_at ? (
                      <>
                        {" · "}
                        {t("última")} <RelativeTime value={rule.last_run_at} t={t} />
                      </>
                    ) : (
                      ` · ${t("nunca se ha ejecutado")}`
                    )}
                  </p>
                </div>
                {canEdit && (
                  <span className="flex items-center gap-0.5 shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-11 md:h-8"
                      onClick={() => openEdit(rule)}
                      aria-label={t("Editar regla {{nombre}}").replace("{{nombre}}", rule.name)}
                    >
                      <Pencil className="w-4 h-4" aria-hidden="true" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="px-2 h-11 md:h-8 text-status-error hover:bg-status-error-soft"
                      onClick={() => setDeleteTarget(rule)}
                      aria-label={t("Eliminar regla {{nombre}}").replace("{{nombre}}", rule.name)}
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <RuleFormModal
        open={formOpen}
        mode={editingId ? "edit" : "create"}
        draft={draft}
        onChange={(next) => {
          setDraft(next);
          if (serverError) setServerError(null);
        }}
        onSubmit={handleSubmit}
        onClose={() => setFormOpen(false)}
        saving={saving}
        serverError={serverError}
      />

      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        size="sm"
        title={t("Eliminar regla")}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("Cancelar")}
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={deleting}>
              {t("Eliminar")}
            </Button>
          </>
        }
      >
        <p className="text-sm text-fg">{t("Vas a eliminar esta regla:")}</p>
        <p className="mt-1 text-sm font-semibold text-fg break-words">{deleteTarget?.name}</p>
        <p className="mt-3 text-sm text-fg-muted">
          {t("Deja de correr desde ya. Lo que ya hizo en las conversaciones se mantiene. No se puede deshacer.")}
        </p>
      </Modal>
    </div>
  );
}
